import moment from 'moment';
import { formatDateUnified } from './dateUtils';

const DATE_PLACEHOLDER_VALUES = new Set([
  '',
  'na',
  'n/a',
  'none',
  'null',
  'undefined',
  'tbd',
  '-',
  '--',
]);

const toIsoDateString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date && Number.isNaN(value.getTime())) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (DATE_PLACEHOLDER_VALUES.has(trimmedValue.toLowerCase())) {
      return '';
    }

    if (
      !/^\d{4}-\d{2}-\d{2}(?:[ T].*)?$/.test(trimmedValue) &&
      Number.isNaN(Date.parse(trimmedValue))
    ) {
      return '';
    }

    return formatDateUnified(trimmedValue, 'iso', { fallback: '' });
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return formatDateUnified(value, 'iso', { fallback: '' });
};

const getHolidayDateValue = (holiday) => {
  if (!holiday) return '';

  if (typeof holiday === 'string') {
    return toIsoDateString(holiday);
  }

  return toIsoDateString(
    holiday.HolidayDate ??
    holiday.holidayDate ??
    holiday.Date ??
    holiday.date
  );
};

const buildHolidaySet = (holidays = []) => (
  new Set(
    (Array.isArray(holidays) ? holidays : [])
      .map(getHolidayDateValue)
      .filter(Boolean)
  )
);

const isBusinessDay = (dateMoment, holidaySet) => {
  const day = dateMoment.day();
  if (day === 0 || day === 6) {
    return false;
  }

  return !holidaySet.has(dateMoment.format('YYYY-MM-DD'));
};

/**
 * Subtract business days from a date, skipping weekends and holidays.
 * @param {string|Date} dateValue - Starting date value
 * @param {number} businessDays - Number of business days to subtract
 * @param {Array} holidays - Holiday records (supports HolidayDate/date variants)
 * @returns {string|null} Result date in YYYY-MM-DD format
 */
export const subtractBusinessDays = (dateValue, businessDays = 0, holidays = []) => {
  if (!dateValue) return null;

  const normalizedDate = toIsoDateString(dateValue);
  if (!normalizedDate) return null;

  const safeBusinessDays = Number.isFinite(Number(businessDays))
    ? Math.max(0, Math.floor(Number(businessDays)))
    : 0;

  let currentDate = moment(normalizedDate, 'YYYY-MM-DD', true);
  if (!currentDate.isValid()) return null;

  if (safeBusinessDays === 0) {
    return currentDate.format('YYYY-MM-DD');
  }

  const holidaySet = buildHolidaySet(holidays);
  let daysRemoved = 0;

  while (daysRemoved < safeBusinessDays) {
    currentDate = currentDate.subtract(1, 'day');
    if (isBusinessDay(currentDate, holidaySet)) {
      daysRemoved += 1;
    }
  }

  while (!isBusinessDay(currentDate, holidaySet)) {
    currentDate = currentDate.subtract(1, 'day');
  }

  return currentDate.format('YYYY-MM-DD');
};

/**
 * Calculate the number of weeks from today until ship date
 * @param {string|Date} shipDate - The current planned ship date
 * @returns {number|null} Number of weeks until ship date, or null if date is invalid
 */
export const calculateWeeksToShip = (shipDate) => {
  if (!shipDate) return null;
  
  const today = moment();
  const shipMoment = moment(shipDate);
  
  if (!shipMoment.isValid()) return null;
  
  // Calculate the difference in weeks from today to ship date
  const weeksToShip = shipMoment.diff(today, 'weeks', true);
  
  // Round to 1 decimal place
  return Math.round(weeksToShip * 10) / 10;
};

/**
 * Get the display color for weeks to ship value
 * @param {number} weeksToShip - Number of weeks to ship
 * @returns {string} CSS color class or style
 */
export const getWeeksToShipColor = (weeksToShip) => {
  if (weeksToShip === null || weeksToShip === undefined) return '';
  
  if (weeksToShip <= 10) {
    return 'text-danger'; // Bootstrap red color class
  }
  
  return 'text-muted'; // Default color
};

/**
 * Calculate the number of weeks from engineering due date to ship date
 * @param {string|Date} engineeringDueDate - The engineering due date
 * @param {string|Date} shipDate - The current planned ship date
 * @returns {number|null} Number of weeks from engineering due to ship, or null if dates are invalid
 */
export const calculateEngineeringWeeks = (engineeringDueDate, shipDate) => {
  if (!engineeringDueDate || !shipDate) return null;
  
  const dueMoment = moment(engineeringDueDate);
  const shipMoment = moment(shipDate);
  
  if (!dueMoment.isValid() || !shipMoment.isValid()) return null;
  
  // Calculate the difference in weeks from engineering due date to ship date
  const engineeringWeeks = shipMoment.diff(dueMoment, 'weeks', true);
  
  // Round to 1 decimal place
  return Math.round(engineeringWeeks * 10) / 10;
};

/**
 * Get the display color for engineering weeks value
 * @param {number} engineeringWeeks - Number of weeks from engineering due to ship
 * @returns {string} CSS color class or style
 */
export const getEngineeringWeeksColor = (engineeringWeeks) => {
  if (engineeringWeeks === null || engineeringWeeks === undefined) return '';
  
  if (engineeringWeeks < 0) {
    return 'text-danger'; // Red for negative (engineering due after ship date)
  } else if (engineeringWeeks <= 2) {
    return 'text-warning'; // Yellow/orange for tight schedule (2 weeks or less)
  } else if (engineeringWeeks <= 4) {
    return 'text-success'; // Green for reasonable schedule (2-4 weeks)
  }
  
  return 'text-muted'; // Default color for longer schedules
};
